import API from "../../services/api";
import Paginator from "../../shared/Paginator";
import { useEffect, useState } from "react";
import { Toast } from "../../shared/Toast";
import { roles, rejectMessOptions } from "../../utils/constants";
import { alphaformatDateTime } from "../../utils/dateFormat";
import { adunitPriceRows } from "../creatives/bookingPrices";
import OrderCreativesChat from "./OrderCreativesChat";

const PAGE_SIZE = 10;

/// booking_slots.status — this queue only surfaces the Finance Pending stage.
/// The backend get-booking-orders endpoint is filtered server-side via `statusFilter`.
const FINANCE_PENDING = 2;

const ORDER_STATUS_META = {
  1: { label: "Pending", cls: "bg-gray-100 text-gray-600 ring-gray-200" },
  2: { label: "Finance Pending", cls: "bg-yellow-100 text-yellow-700 ring-yellow-200" },
  3: { label: "Approved", cls: "bg-green-100 text-green-700 ring-green-200" },
  4: { label: "Rejected", cls: "bg-red-100 text-red-700 ring-red-200" },
  5: { label: "Cancelled", cls: "bg-gray-100 text-gray-500 ring-gray-200" },
};

/// rejectMessOptions is 1-indexed ([0] is a blank placeholder); the backend stores
/// the selected indices and rehydrates labels via rejectMessOptions[index].
const REJECT_REASONS = rejectMessOptions
  .map((label, index) => ({ index, label }))
  .filter((r) => r.index > 0 && r.label);

/// amounts are INR — group them the Indian way so a large RO value is readable at a glance
const formatINR = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(n)) return null;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

/// booking dates are stored as unix seconds
const fmtDate = (epoch, type = "start") => alphaformatDateTime(epoch, type).dateStr;

const Dash = () => <span className="text-gray-300">—</span>;

const StatusBadge = ({ status }) => {
  const meta = ORDER_STATUS_META[status] ?? { label: "Unknown", cls: "bg-gray-100 text-gray-600 ring-gray-200" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
};

const RoLink = ({ url }) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-600 hover:underline">
      View
    </a>
  ) : (
    <span className="text-xs">
      <Dash />
    </span>
  );

/// Per-ad-unit breakdown with BOTH prices — selling and actual (rate card). Finance is
/// an approver, so it sees the card price the sales person never does. Read-only;
/// a load failure just hides the section, it never blocks the decision.
const AdUnitPrices = ({ slotId }) => {
  const [units, setUnits] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    API({ url: "/slotbooking/get-order-adunits", method: "POST", responseType: "json", data: { slot_id: slotId } })
      .then((res) => {
        if (!live) return;
        if (res.code === 200) setUnits(res.data?.data || []);
        else setFailed(true);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [slotId]);

  if (failed) return null;
  if (units === null) return <p className="text-xs text-gray-400">Loading ad units…</p>;
  if (!units.length) return null;

  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Ad units — card rate vs sales price</p>
      <ul className="space-y-2">
        {units.map((au) => (
          <li key={au.internal_id} className="rounded-xl border border-gray-100 p-3 text-sm">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="font-medium text-gray-800">
                {au.creative_type || "Ad unit"} · {au.campaign_type}
              </span>
              {au.discounted && (
                <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-200">
                  Discount
                </span>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
              {adunitPriceRows(au).map((r) => (
                <div key={r.key} className="min-w-0">
                  <dt className="text-xs text-gray-500">{r.label}</dt>
                  <dd className="text-gray-900">{r.value}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ActionModal = ({ row, onClose, onDone }) => {
  const [action, setAction] = useState(""); // "approve" | "reject"
  const [reasons, setReasons] = useState([]); // selected reject reason indices
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isReject = action === "reject";

  const toggleReason = (index) =>
    setReasons((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));

  const selectAction = (opt) => {
    setAction(opt);
    setError("");
    if (opt !== "reject") {
      setReasons([]);
      setDescription("");
    }
  };

  const submit = async () => {
    if (!action) return setError("Select an action.");
    if (isReject) {
      if (!reasons.length) return setError("Select at least one rejection reason.");
      if (!description.trim()) return setError("A description is required for rejection.");
    }
    setError("");
    setLoading(true);
    try {
      const url = isReject ? "/slotbooking/reject-booking" : "/slotbooking/approve-booking";
      const data = isReject
        ? { slot_id: row.order_id, reason: reasons, description: description.trim() }
        : { slot_id: row.order_id };
      const res = await API({ url, method: "POST", responseType: "json", data });
      if (res.code === 200) {
        Toast("success", res.data || res.message || (isReject ? "Order rejected." : "Order approved."));
        onDone();
      } else {
        Toast("error", res.data?.message || res.data || res.message || "Action failed. Please try again.");
      }
    } catch (e) {
      Toast("error", e?.data?.message || e?.message || "Action failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
      >
        {/* header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Review sales order</h3>
            <p className="mt-0.5 truncate font-mono text-xs text-gray-500">Order #{row.order_ref || row.order_id}</p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            className="-mr-1 rounded-lg p-1 text-2xl leading-none text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            &times;
          </button>
        </div>

        {/* body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Brand</dt>
              <dd className="mt-0.5 truncate text-gray-800">{row.brand_name || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Budget</dt>
              <dd className="mt-0.5 font-semibold text-gray-900">{formatINR(row.ro_value) ?? "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Company</dt>
              <dd className="mt-0.5 truncate text-gray-800">{row.client_name || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Campaign</dt>
              <dd className="mt-0.5 truncate text-gray-800">{row.campaign_name || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Sales Person</dt>
              <dd className="mt-0.5 truncate text-gray-800">{row.sales_person?.name || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Duration</dt>
              <dd className="mt-0.5 text-gray-800">
                {fmtDate(row.start_date)} <span className="text-gray-400">→</span> {fmtDate(row.end_date, "end")}
              </dd>
            </div>
          </dl>

          <AdUnitPrices slotId={row.order_id} />

          {row.rofile_url && (
            <a
              href={row.rofile_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
            >
              <sl-icon library="lucide" name="file-text"></sl-icon> View RO document
            </a>
          )}

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Decision</p>
            <div className="flex gap-3">
              {[
                { opt: "approve", label: "✓ Approve" },
                { opt: "reject", label: "✕ Reject" },
              ].map(({ opt, label }) => (
                <button
                  key={opt}
                  onClick={() => selectAction(opt)}
                  aria-pressed={action === opt}
                  className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold transition-all
                    ${
                      action === opt
                        ? opt === "approve"
                          ? "border-green-600 bg-green-600 text-white shadow-sm"
                          : "border-red-600 bg-red-600 text-white shadow-sm"
                        : opt === "approve"
                          ? "border-gray-300 text-gray-600 hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                          : "border-gray-300 text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {isReject && (
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="mb-2 text-xs font-medium text-gray-600">
                Rejection reason <span className="text-red-500">*</span>
              </p>
              <div className="space-y-2">
                {REJECT_REASONS.map((r) => (
                  <label key={r.index} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={reasons.includes(r.index)}
                      onChange={() => toggleReason(r.index)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Description {isReject && <span className="text-red-500">*</span>}
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isReject ? "Why is this being rejected?" : "Add a note (optional)..."}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        {/* footer */}
        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading || !action}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50
              ${isReject ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}
          >
            {loading && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            )}
            {loading ? "Submitting..." : isReject ? "Confirm rejection" : "Confirm approval"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FinanceOrderApprovals = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(null);
  const [chatRow, setChatRow] = useState(null);

  const isFinanceUser = localStorage.getItem("role") === roles.financer;

  const getData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await API({
        /// dedicated finance-gated route (ops + financer); reuses the CST orders
        /// query but scopes financers to the orders routed to them.
        url: "/slotbooking/get-finance-orders",
        method: "POST",
        responseType: "json",
        /// `page` is 0-indexed; `statusFilter` restricts the list to the finance queue.
        data: { page: currentPage, limit: PAGE_SIZE, statusFilter: FINANCE_PENDING },
      });
      if (res.code === 200) {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        const count = Number(res.data?.total ?? 0);
        const pages = Math.ceil(count / PAGE_SIZE);
        setTotalCount(count);
        setTotalPages(pages);
        setData(rows);
        /// actioning the last row on the last page shrinks the range — step back instead of stranding the user
        const lastPage = Math.max(pages - 1, 0);
        if (currentPage > 0 && currentPage > lastPage) setCurrentPage(lastPage);
      } else {
        setData([]);
        setTotalPages(0);
        setTotalCount(0);
        const message = res.data?.message || res.message || "Unable to load orders. Please try again.";
        setLoadError(message);
        Toast("error", message);
      }
    } catch (e) {
      setData([]);
      setTotalPages(0);
      setTotalCount(0);
      const message = e?.data?.message || e?.message || "Unable to load orders. Please try again.";
      setLoadError(message);
      Toast("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (event) => setCurrentPage(event.selected);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const renderAction = (r) =>
    r.order_status === FINANCE_PENDING && isFinanceUser ? (
      <button
        onClick={() => setSelected(r)}
        className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
      >
        Review
      </button>
    ) : (
      <span className="text-xs text-gray-400">—</span>
    );

  /// order -> its creatives -> chat; the badge sums this viewer's unread across the order's creatives
  const renderChat = (r) => {
    const unread = Number(r.unread_count) || 0;
    const empty = !r.total_creatives;
    return (
      <button
        type="button"
        onClick={() => setChatRow(r)}
        disabled={empty}
        title={empty ? "No creatives on this order" : "Open creatives chat"}
        className="relative inline-flex rounded-md p-1 text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
      >
        <sl-icon library="lucide" name="message-circle" style={{ fontSize: "18px" }}></sl-icon>
        {unread > 0 && (
          <span className="absolute -right-2 -top-2 min-w-[18px] rounded-full bg-green-500 px-1 text-center text-[10px] font-semibold leading-[18px] text-white shadow">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
    );
  };

  const cols = [
    {
      label: "#",
      render: (_, i) => <span className="text-xs text-gray-400">{currentPage * PAGE_SIZE + i + 1}</span>,
      align: "text-right",
      width: "w-12",
    },
    {
      label: "Order ID",
      render: (r) =>
        r.order_ref ? (
          <span title={r.order_ref} className="block max-w-[160px] truncate font-mono text-xs text-gray-800">
            {r.order_ref}
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: "Sales Person",
      render: (r) => (r.sales_person?.name ? <span className="text-sm text-gray-700">{r.sales_person.name}</span> : <Dash />),
    },
    { label: "Brand", render: (r) => (r.brand_name ? <span className="text-sm text-gray-700">{r.brand_name}</span> : <Dash />) },
    {
      label: "Company",
      render: (r) =>
        r.client_name ? (
          <span title={r.client_name} className="block max-w-[160px] truncate text-sm text-gray-700">
            {r.client_name}
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: "Campaign",
      render: (r) =>
        r.campaign_name ? (
          <span title={r.campaign_name} className="block max-w-[160px] truncate text-sm text-gray-700">
            {r.campaign_name}
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: "Duration",
      render: (r) => (
        <span className="whitespace-nowrap text-xs text-gray-600">
          {fmtDate(r.start_date)} <span className="text-gray-400">→</span> {fmtDate(r.end_date, "end")}
        </span>
      ),
    },
    {
      label: "Budget",
      render: (r) => <span className="font-semibold tabular-nums text-gray-900">{formatINR(r.ro_value) ?? <Dash />}</span>,
      align: "text-right",
    },
    { label: "RO", render: (r) => <RoLink url={r.rofile_url} />, align: "text-center" },
    { label: "Status", render: (r) => <StatusBadge status={r.order_status} /> },
    { label: "Chat", render: renderChat, align: "text-center" },
    { label: "Action", render: renderAction, align: "text-right" },
  ];

  const skeletonRows = Array.from({ length: 5 });

  return (
    <div className="w-full max-w-full overflow-x-hidden p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Order Approvals</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {loading
              ? "Loading finance-pending orders..."
              : loadError
                ? "Could not load orders."
                : `${totalCount} order${totalCount === 1 ? "" : "s"} awaiting finance approval${
                    totalPages > 1 ? ` · page ${currentPage + 1} of ${totalPages}` : ""
                  }`}
          </p>
        </div>
        {!isFinanceUser && !loading && !loadError && (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">View only</span>
        )}
      </div>

      {loadError && !loading && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{loadError}</p>
          <button
            onClick={getData}
            className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* ---------- md+ : full table, sticky header, scrolls inside its own box ----------
          Uses `max-md:hidden`, NOT `hidden md:block`: Styles.scss defines a global
          `.hidden{display:none!important}` that `.md\:block` can never override. */}
      <div className="max-md:hidden overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
              <tr>
                {cols.map((c) => (
                  <th key={c.label} className={`whitespace-nowrap px-4 py-3 font-medium ${c.align ?? "text-left"} ${c.width ?? ""}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                skeletonRows.map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {cols.map((c) => (
                      <td key={c.label} className="px-4 py-3.5">
                        <div className="h-3 animate-pulse rounded bg-gray-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={cols.length} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-500">
                      {loadError ? "Couldn't load records." : "No orders awaiting finance approval."}
                    </p>
                    {!loadError && <p className="mt-1 text-xs text-gray-400">New finance-pending orders will show up here.</p>}
                  </td>
                </tr>
              ) : (
                data.map((row, i) => (
                  <tr key={row.order_id} className="transition-colors hover:bg-gray-50">
                    {cols.map((c) => (
                      <td key={c.label} className={`whitespace-nowrap px-4 py-3 align-middle ${c.align ?? "text-left"}`}>
                        {c.render(row, i)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- below md : one card per record ---------- */}
      <div className="space-y-3 md:hidden">
        {loading ? (
          skeletonRows.map((_, i) => (
            <div key={`skc-${i}`} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center">
            <p className="text-sm font-medium text-gray-500">
              {loadError ? "Couldn't load records." : "No orders awaiting finance approval."}
            </p>
            {!loadError && <p className="mt-1 text-xs text-gray-400">New finance-pending orders will show up here.</p>}
          </div>
        ) : (
          data.map((row) => (
            <div key={row.order_id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-[11px] uppercase tracking-wide text-gray-400">#{row.order_ref || row.order_id}</p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-800">{row.brand_name || "Unnamed brand"}</p>
                </div>
                <StatusBadge status={row.order_status} />
              </div>

              <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-lg font-semibold tabular-nums text-gray-900">{formatINR(row.ro_value) ?? "—"}</span>
                {row.campaign_name && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{row.campaign_name}</span>
                )}
              </div>

              <dl className="mt-3 space-y-1.5 border-t border-gray-100 pt-3 text-xs text-gray-500">
                <div className="flex justify-between gap-3">
                  <dt>Company</dt>
                  <dd className="truncate text-gray-700">{row.client_name || "—"}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Sales Person</dt>
                  <dd className="truncate text-gray-700">{row.sales_person?.name || "—"}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Duration</dt>
                  <dd className="text-right text-gray-700">
                    {fmtDate(row.start_date)} → {fmtDate(row.end_date, "end")}
                  </dd>
                </div>
              </dl>

              <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                <RoLink url={row.rofile_url} />
                <div className="flex items-center gap-3">
                  {renderChat(row)}
                  {renderAction(row)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />

      {chatRow && (
        <OrderCreativesChat
          row={chatRow}
          onClose={(remaining) => {
            if (remaining !== undefined) {
              setData((prev) => prev.map((r) => (r.order_id === chatRow.order_id ? { ...r, unread_count: remaining } : r)));
            }
            setChatRow(null);
          }}
        />
      )}

      {selected && (
        <ActionModal
          row={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            getData();
          }}
        />
      )}
    </div>
  );
};

export default FinanceOrderApprovals;
